import { createFileRoute } from "@tanstack/react-router";
import { NanaeHeader, NanaeFooter } from "@/components/qv3/NanaeShared";
import { LegalSection } from "@/components/qorum/LegalSection";
import { openCookiePreferences } from "@/lib/cookie-consent";
import { socialMeta, pageJsonLd } from "@/lib/seo-meta";

export const Route = createFileRoute("/politique-cookies")({
  head: () => ({
    meta: [
      { title: "Politique cookies — Hōjin" },
      {
        name: "description",
        content:
          "Catégories de cookies utilisés par Hōjin, finalités, durées et modalités de retrait du consentement.",
      },
      ...socialMeta({
        title: "Politique cookies — Hōjin",
        description: "Catégories de cookies utilisés par Hōjin, finalités, durées et modalités de retrait du consentement.",
        url: "https://hojin.ai/politique-cookies",
        image: "conseil",
        card: "summary",
      }),
    ],
    links: [{ rel: "canonical", href: "https://hojin.ai/politique-cookies" }],
    scripts: pageJsonLd({
      path: "/politique-cookies",
      name: "Politique cookies — Hōjin",
      description: "Catégories de cookies utilisés par Hōjin, finalités, durées et modalités de retrait du consentement.",
      type: "WebPage",
      image: "conseil",
      breadcrumb: [{ name: "Politique cookies", path: "/politique-cookies" }],
    }),
  }),
  component: CookiesPage,
});

const SECTIONS = [
  {
    title: "Catégories de cookies",
    body: (
      <>
        <p>
          <strong>Essentiels (toujours actifs).</strong> Nécessaires à
          l'authentification et au fonctionnement du service. Déposés sans
          consentement. Finalité : maintien de session. Durée : la durée de la
          session, ou jusqu'à 30 jours pour la persistance de connexion.
        </p>
        <p>
          <strong>Mesure & monitoring (sur consentement).</strong> Outils de
          détection d'erreurs (Sentry) destinés à stabiliser le service.
          Désactivés par défaut. Durée : jusqu'à 12 mois.
        </p>
      </>
    ),
  },
  {
    title: "Finalités",
    body: (
      <p>
        Les cookies non-essentiels servent uniquement à diagnostiquer les
        anomalies techniques afin d'améliorer la qualité du service. Aucune
        publicité, aucun traçage commercial.
      </p>
    ),
  },
  {
    title: "Finalités",
    body: (
      <p>
        Les cookies non-essentiels servent uniquement à diagnostiquer les
        anomalies techniques afin d'améliorer la qualité du service. Aucune
        publicité, aucun traçage commercial.
      </p>
    ),
  },
  {
    title: "Traceurs plateforme d'hébergement (Lovable)",
    body: (
      <>
        <p>
          Hōjin est hébergée sur la plateforme Lovable, qui dépose
          automatiquement, dès le chargement de l'application et indépendamment
          de notre bandeau de consentement, deux traceurs techniques que nous
          ne pouvons pas désactiver depuis notre code applicatif :
        </p>
        <ul style={{ paddingLeft: 20, lineHeight: 1.6 }}>
          <li>
            <strong>Session replay — rrweb</strong> (requête{" "}
            <code>/__l5e/replay</code>). Enregistre les interactions UI
            (mouvements, clics, saisies, navigation) à des fins de
            reproduction d'anomalies et d'amélioration produit côté
            plateforme d'hébergement. Durée de conservation : définie par
            l'hébergeur.
          </li>
          <li>
            <strong>Analytics anonyme Lovable</strong> (cookie/identifiant{" "}
            <code>__lovable_anonymous_id</code>, requête{" "}
            <code>/__l5e/trackevents</code>). Mesure d'audience pseudonymisée
            (événements de navigation, agrégats d'usage) opérée par la
            plateforme d'hébergement. Durée de conservation : définie par
            l'hébergeur.
          </li>
        </ul>
        <p>
          <strong>Statut.</strong> Ces traceurs sont considérés comme{" "}
          <em>non strictement nécessaires</em> au sens de la CNIL et devraient
          en principe être conditionnés à votre consentement. Ils sont activés
          par défaut par la plateforme d'hébergement et ne peuvent pas, à ce
          jour, être gatés depuis notre application. Une demande de
          désactivation / mise sous consentement a été adressée à l'éditeur de
          la plateforme. Cette mention sera retirée dès que ces traceurs
          seront effectivement conditionnés à votre choix exprimé via le
          bandeau ci-dessus, ou supprimés.
        </p>
        <p>
          <strong>Base légale.</strong> Intérêt légitime de l'hébergeur à
          assurer le bon fonctionnement, la sécurité et l'amélioration de sa
          plateforme (art. 6.1.f RGPD), en l'attente d'une mise en conformité
          consentement (art. 82 LIL / directive ePrivacy). Si vous refusez ces
          traceurs, contactez-nous via les coordonnées indiquées dans les{" "}
          mentions légales : nous transmettrons votre demande d'opposition à
          l'hébergeur.
        </p>
      </>
    ),
  },
  {
    title: "Retrait du consentement",
    body: (
      <p>
        Vous pouvez retirer ou modifier votre consentement à tout moment depuis
        le lien « Gérer les cookies » présent en pied de page, ou en cliquant
        ci-dessous.
      </p>
    ),
  },
];




export function CookiesPage() {
  return (
    <div className="nanae-page" style={{ background: "var(--ivory)", minHeight: "100vh" }}>
      <NanaeHeader title="Politique cookies" variant="public" />
      <section
        aria-label="Politique cookies"
        style={{ maxWidth: 720, margin: "0 auto", padding: "64px 32px 80px" }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 20,
          }}
        >
          <span
            aria-hidden
            style={{ width: 24, height: 0.5, background: "var(--accent-cramoisi)", display: "inline-block" }}
          />
          <span
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: 10,
              color: "var(--gold-deep)",
              letterSpacing: "3px",
              textTransform: "uppercase",
            }}
          >
            Cookies · Volume MMXXVI
          </span>
        </div>
        <h1
          style={{
            fontFamily: '"Anton", sans-serif',
            fontWeight: 400,
            fontSize: 64,
            color: "var(--charcoal)",
            letterSpacing: "-1.5px",
            textTransform: "uppercase",
            lineHeight: 0.95,
            margin: "0 0 32px",
          }}
        >
          Cookies
        </h1>
        <LegalSection articles={SECTIONS} />
        <button
          type="button"
          onClick={() => openCookiePreferences()}
          style={{
            marginTop: 24,
            background: "var(--charcoal)",
            color: "var(--ivory)",
            border: "1px solid var(--accent-cramoisi)",
            padding: "12px 18px",
            fontFamily: '"DM Mono", monospace',
            fontSize: 11,
            letterSpacing: "2px",
            textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          Gérer mes cookies
        </button>
      </section>
      <NanaeFooter variant="full" />
    </div>
  );
}
